/**
 * Tandem — the "Points model" page. A read-only explanation: it replaces a manual
 * spreadsheet whose owner could always say why a number was what it was, so the
 * job is to let anyone read how a score was arrived at in under a minute.
 *
 * No controls, because the values are agreed and offering to change them implies
 * otherwise — every value the model uses is stated here instead.
 */
import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { T } from "./tokens";
import { usePointsConfig } from "./pointsConfig";
import { checkMonthlyTarget, fmtPoints } from "./points";

const Section = ({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) => (
    <Box component="section" sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px 20px" }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 800, color: T.ink2 }}>{title}</Typography>
        {sub ? (
            <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "2px", mb: "10px", lineHeight: 1.5 }}>{sub}</Typography>
        ) : (
            <Box sx={{ height: "10px" }} />
        )}
        {children}
    </Box>
);

/** A rule, what it pays, and why. */
const Line = ({ label, value, note }: { label: string; value?: string; note?: string }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", padding: "6px 0", borderTop: `1px solid ${T.borderSoft}` }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: T.ink2, display: "inline" }}>{label}</Typography>
            {note && <Typography sx={{ fontSize: "11.5px", color: T.muted, display: "inline", ml: "7px" }}>{note}</Typography>}
        </Box>
        {value && (
            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.purpleDeep, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                {value}
            </Typography>
        )}
    </Box>
);

const Caveat = ({ title, children }: { title: string; children: ReactNode }) => (
    <Box sx={{ display: "flex", gap: "10px", padding: "7px 0", borderTop: `1px solid ${T.borderSoft}` }}>
        <Box sx={{ color: T.amber, fontSize: "12.5px", fontWeight: 800, flexShrink: 0, lineHeight: 1.5 }}>!</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: T.ink2 }}>{title}</Typography>
            <Typography sx={{ fontSize: "12.5px", color: T.muted, lineHeight: 1.55 }}>{children}</Typography>
        </Box>
    </Box>
);

const PointsSettings = () => {
    const { config } = usePointsConfig();
    const target = checkMonthlyTarget(config);

    const rates = Object.entries(config.industryRates).sort((a, b) => a[0].localeCompare(b[0]));
    const half = rates.filter(([, r]) => r <= 1);
    const full = rates.filter(([, r]) => r > 1);
    const sup = config.supplementPerExtraAssessment;
    const blank = config.scoreUnknownIndustryAs;
    const charity = config.industryRates.Charity ?? 1;

    return (
        <Box sx={{ padding: "24px 28px 56px" }}>
            <Box sx={{ maxWidth: "720px", display: "flex", flexDirection: "column", gap: "14px" }}>
                <Typography sx={{ fontSize: "13.5px", color: T.muted, lineHeight: 1.6 }}>
                    A visit scores from its Salesforce visit type and its account. Visits at the same account on the
                    same day count as one <b>occasion</b>. Every points figure on the Schedule and the Report comes
                    from this.
                </Typography>

                {/* One visit */}
                <Section title="What a visit scores" sub="First line that applies decides it">
                    <Line label="Fact find" value="—" note="not scored, and does not count towards a day" />
                    <Line label="Remote visit" value={fmtPoints(config.remotePoints)} note={`${config.remoteBatchSize} fill a day`} />
                    <Line label="Workplace inspection" value={fmtPoints(config.wpiPoints)} note="flat, any industry" />
                    <Line label="Consultancy — full day" value={fmtPoints(config.consultancyFullPoints)} note="by length, not industry" />
                    <Line label="Consultancy — half day" value={fmtPoints(config.consultancyHalfPoints)} />
                    <Line label="Consultancy — length not stated" value={fmtPoints(config.consultancyUnspecPoints)} note="assumed a full day" />
                    <Line label="Fire assessment, High Risk site" value={fmtPoints(config.highRiskFraPoints)} note="the site's risk band beats its industry rate" />
                    <Line label="Anything else" note="takes the industry rate below" />
                    <Typography sx={{ fontSize: "11.5px", color: T.muted2, mt: "12px", lineHeight: 1.55 }}>
                        A site counts as high risk if <b>either</b> the FRA Risk Level picklist says so or the older
                        High Risk FRA checkbox is ticked. Both are read: the picklist defaults itself to “Low Risk”,
                        so that value does not mean the site was assessed, and the checkbox is only ever ticked
                        deliberately. The two disagree on about 8% of accounts and are worth reconciling in
                        Salesforce.
                    </Typography>
                </Section>

                {/* Industry */}
                <Section title="Industry rates" sub="Half a day or a full day, from the median time actually spent on site">
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "0 24px" }}>
                        {[
                            { heading: `Half a day · ${fmtPoints(1)}`, list: half },
                            { heading: `A full day · ${fmtPoints(2)}`, list: full },
                        ].map(({ heading, list }) => (
                            <Box key={heading}>
                                <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: T.muted, mt: "4px", mb: "1px" }}>
                                    {heading}
                                </Typography>
                                {list.map(([industry]) => (
                                    <Typography key={industry} sx={{ fontSize: "12.5px", color: T.ink2, padding: "4px 0", borderTop: `1px solid ${T.borderSoft}` }}>
                                        {industry}
                                    </Typography>
                                ))}
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: T.muted, mt: "12px", lineHeight: 1.55 }}>
                        Matched exactly against Salesforce <b>Account.Industry</b>. Around a third of accounts have
                        none set — those visits score <b>{blank == null ? "nothing" : fmtPoints(blank)}</b> and go
                        amber on the schedule, so the account gets corrected at the next fact find or visit.
                    </Typography>
                </Section>

                {/* One day */}
                <Section title="What a day scores" sub="Same account, same day = one occasion">
                    <Line label="The occasion" value={`highest, not the sum`} note="of its visits' points" />
                    <Line label="Each additional report" value={`+ ${fmtPoints(sup)}`} note="no upper limit" />
                    <Line label="Recognition score" note="the occasion total — this is what gets reported" />
                    <Line label="Capacity cost" note="the same figure without the supplement — what the day is consumed by" />

                    <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "12px", lineHeight: 1.6 }}>
                        The two differ because a GRA+FRA takes no longer on site than a GRA alone: the demand is
                        still half a day, but two reports come out of it. Keeping the supplement out of capacity is
                        what leaves the day free to pair with other work.
                    </Typography>
                    <Box sx={{ mt: "10px", background: T.rowTint, border: `1px solid ${T.borderSoft}`, borderRadius: "9px", padding: "10px 12px" }}>
                        <Typography sx={{ fontSize: "12.5px", color: T.muted, lineHeight: 1.55 }}>
                            <b>A GRA and an FRA at one Charity site, same day.</b> Base {fmtPoints(charity)} plus{" "}
                            {fmtPoints(sup)} = <b>{fmtPoints(charity + sup)} recognition</b>, {fmtPoints(charity)}{" "}
                            capacity, still pairable. One combined record or two separate ones scores the same — the
                            supplement counts reports, not records.
                        </Typography>
                    </Box>
                </Section>

                {/* Targets */}
                <Section title="Targets">
                    <Line
                        label="Monthly target"
                        value={String(config.monthlyPointsTarget)}
                        note={`points per consultant · ${target.fieldDaysPerMonth.toFixed(0)} field days a month`}
                    />
                </Section>

                {/* Gaps */}
                <Section title="What the score can't account for yet">
                    <Caveat title="No day saving is claimed yet">
                        Pairing two half-day jobs onto one day is a drive-time question, so it is planned using
                        Google's routing API rather than guessed from postcodes. Until that is in, pairing, days
                        saved and any FTE figure are simply not calculated — no half-finished estimate is shown.
                        The open dependency is a reliable start location for each consultant.
                    </Caveat>
                </Section>
            </Box>
        </Box>
    );
};

export default PointsSettings;
